/**
 * schemas/cash/transaction.ts
 * ──────────────────────────────────────────────────────────────────────────
 * One row of the append-only ledger.
 *
 * There is no update and no delete anywhere in this file, and none on the API
 * either: a correction is a NEW row with `is_reversal: true` pointing at what
 * it reverses. `reversed_by_id` is how the drawer knows to hide the Reverse
 * button — a row can only be reversed once.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { z } from 'zod';
import { currency, isoDate, isoDateNullable, minor } from '../common';

/** The types a client may POST. `reversal` is created by the API, never sent. */
export const TXN_TYPES = [
  'allocation',
  'spend',
  'collection',
  'deposit',
  'replenishment',
  'return',
  'adjustment',
] as const;
export type TxnType = (typeof TXN_TYPES)[number];

/** The four a custodian may record against their own cash. */
export const CUSTODIAN_TXN_TYPES = ['spend', 'collection', 'deposit', 'replenishment'] as const;

export const transactionSchema = z.object({
  id: z.string(),
  txn_type: z.string(),
  /** Always positive. Direction comes from the type, never from a minus sign. */
  amount_minor: minor,
  /** The same amount with its direction applied — what the ledger renders. */
  signed_minor: minor,
  cur: currency,
  category_id: z.string().nullable(),
  memo: z.string().nullable(),
  /** Evidence. Its presence is what the `noReceipt` approval rule tests. */
  document_ref: z.string().nullable(),
  /** For a collection: which Billing item it settles. */
  owed_ref: z.string().nullable(),
  is_reversal: z.boolean(),
  reverses_id: z.string().nullable(),
  /** Non-null once corrected — the drawer hides Reverse on these rows. */
  reversed_by_id: z.string().nullable(),
  route_target: z.string().nullable(),
  route_ref: z.string().nullable(),
  routed_at: isoDateNullable,
  occurred_at: isoDate,
});
export type Transaction = z.infer<typeof transactionSchema>;

export const transactionsResponseSchema = z.object({ transactions: z.array(transactionSchema) });

/** POST /v1/cash/transactions */
export const transactionCreateSchema = z.object({
  assignment_id: z.string(),
  txn_type: z.enum(TXN_TYPES),
  amount_minor: z.string(),
  category_id: z.string().optional(),
  memo: z.string().max(240).optional(),
  document_ref: z.string().max(200).optional(),
  owed_ref: z.string().max(200).optional(),
  expected_minor: z.string().optional(),
  occurred_at: z.string().optional(),
});
export type TransactionCreate = z.infer<typeof transactionCreateSchema>;

/**
 * What POST /v1/cash/transactions returns. `approval` is present when the
 * engine held the spend — the UI says so rather than implying the money moved.
 */
export const recordResultSchema = z.object({
  id: z.string(),
  created: z.boolean(),
  balance_minor: minor,
  approval: z
    .object({
      id: z.string(),
      needs: z.number(),
      state: z.string(),
      rule_summary: z.string().nullable(),
    })
    .optional(),
  routed_to: z.string().optional(),
  outbox_id: z.string().optional(),
});
export type RecordResult = z.infer<typeof recordResultSchema>;
